<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  gateKey: { type: String, required: true },
  enabled: { type: Boolean, default: true },
  severity: {
    type: String,
    default: 'info',
    validator: value => ['critical', 'warning', 'info'].includes(value),
  },
  fields: { type: Array, default: () => [] },
  values: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle', 'updateField']);

const { t } = useI18n();

const severityColors = {
  critical: 'border-l-ruby-9 bg-ruby-3',
  warning: 'border-l-amber-9 bg-amber-3',
  info: 'border-l-iris-9 bg-iris-3',
};

const severityBadgeColors = {
  critical: 'bg-ruby-9 text-white',
  warning: 'bg-amber-9 text-white',
  info: 'bg-iris-9 text-white',
};

const severityLabels = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
};

const cardClass = computed(() => {
  if (!props.enabled) {
    return 'border-l-n-weak bg-n-solid-2 opacity-60';
  }
  return severityColors[props.severity] || severityColors.info;
});

const title = computed(() => {
  const key = props.gateKey.toUpperCase();
  return t(`CAPTAIN.ASSISTANTS.SAFETY_GATES.GATES.${key}.TITLE`);
});

const description = computed(() => {
  const key = props.gateKey.toUpperCase();
  return t(`CAPTAIN.ASSISTANTS.SAFETY_GATES.GATES.${key}.DESCRIPTION`);
});

const getFieldLabel = fieldKey => {
  const key = fieldKey.toUpperCase();
  return t(`CAPTAIN.ASSISTANTS.SAFETY_GATES.FIELDS.${key}`);
};

const handleNumberInput = (field, event) => {
  const raw = event.target.value;
  if (raw === '') return;
  const num = Number(raw);
  if (Number.isNaN(num)) return;
  const clamped = Math.min(
    Math.max(num, field.min ?? 0),
    field.max ?? Infinity
  );
  emit('updateField', field.key, clamped);
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-xl border-l-4 outline outline-1 outline-n-container transition-all"
    :class="cardClass"
  >
    <div class="flex items-center justify-between">
      <div class="flex flex-col gap-0.5">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">{{ title }}</span>
          <span
            class="text-[10px] font-medium px-1.5 py-0.5 rounded-full uppercase tracking-wide"
            :class="severityBadgeColors[severity]"
          >
            {{ severityLabels[severity] }}
          </span>
        </div>
        <span class="text-xs text-n-slate-11">{{ description }}</span>
      </div>
      <label
        class="relative inline-flex items-center cursor-pointer flex-shrink-0 ml-4"
      >
        <input
          type="checkbox"
          :checked="enabled"
          class="sr-only peer"
          @change="emit('toggle', $event.target.checked)"
        />
        <div
          class="w-9 h-5 bg-n-strong rounded-full peer peer-checked:bg-iris-9 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"
        />
      </label>
    </div>
    <div
      v-if="enabled && fields.length > 0"
      class="flex flex-wrap gap-3 pt-2 border-t border-n-weak"
    >
      <div v-for="field in fields" :key="field.key" class="flex flex-col gap-1">
        <label class="text-xs text-n-slate-11">
          {{ getFieldLabel(field.key) }}
        </label>
        <select
          v-if="field.type === 'select'"
          :value="values[field.key]"
          class="text-sm px-2 py-1.5 rounded-lg border border-n-strong bg-n-solid-2 text-n-slate-12 outline-none focus:border-iris-9"
          @change="emit('updateField', field.key, $event.target.value)"
        >
          <option v-for="opt in field.options" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </select>
        <div v-else class="flex items-center gap-1">
          <span v-if="field.suffix" class="text-sm text-n-slate-11">
            {{ field.suffix }}
          </span>
          <input
            type="number"
            :value="values[field.key]"
            :min="field.min"
            :max="field.max"
            class="w-20 text-sm px-2 py-1.5 rounded-lg border border-n-strong bg-n-solid-2 text-n-slate-12 outline-none focus:border-iris-9"
            @input="handleNumberInput(field, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
